import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, MapPin, Truck, Route as RouteIcon, Layers } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const STATUS_COLORS = {
  'Notificada': '#3B82F6',
  'Asignada': '#F59E0B',
  'En Ruta': '#8B5CF6',
  'Recogida': '#10B981',
  'Completada': '#6B7280'
};

export default function AdminMapa() {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layersRef = useRef({});
  
  const [loading, setLoading] = useState(true);
  const [syncingArcGIS, setSyncingArcGIS] = useState(false);
  const [layers, setLayers] = useState({ solicitudes: [], routes: [], gps_vehicles: [] });
  const [visibleLayers, setVisibleLayers] = useState({
    solicitudes: true,
    routes: true,
    vehicles: true
  });

  useEffect(() => {
    // Initialize map
    if (!mapInstance.current && mapRef.current) {
      mapInstance.current = L.map(mapRef.current).setView([40.4732, -3.8726], 13);
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapInstance.current);
      
      layersRef.current = {
        solicitudes: L.layerGroup().addTo(mapInstance.current),
        routes: L.layerGroup().addTo(mapInstance.current),
        vehicles: L.layerGroup().addTo(mapInstance.current)
      };
    }
    
    fetchMapLayers();
    
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  useEffect(() => {
    updateMapLayers();
  }, [layers, visibleLayers]);

  const fetchMapLayers = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/map-layers`, { headers: authHeaders() });
      setLayers(resp.data);
    } catch (err) {
      toast.error('Error al cargar capas del mapa');
    } finally {
      setLoading(false);
    }
  };

  const updateMapLayers = () => {
    if (!mapInstance.current) return;
    
    // Clear layers
    Object.values(layersRef.current).forEach(layer => layer.clearLayers());
    
    // Add solicitudes markers
    if (visibleLayers.solicitudes) {
      layers.solicitudes.forEach(sol => {
        if (sol.latitude && sol.longitude) {
          const marker = L.circleMarker([sol.latitude, sol.longitude], {
            radius: 8,
            fillColor: STATUS_COLORS[sol.estado] || '#3B82F6',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 0.8
          });
          
          marker.bindPopup(`
            <strong>${sol.ticket_code}</strong><br/>
            Estado: ${sol.estado}<br/>
            Zona: ${sol.zona || 'N/A'}<br/>
            Tipo: ${sol.tipo_enseres || 'N/A'}
          `);
          
          marker.addTo(layersRef.current.solicitudes);
        }
      });
    }
    
    // Add routes polylines
    if (visibleLayers.routes) {
      layers.routes.forEach(route => {
        if (route.sequence && route.sequence.length > 0) {
          const latlngs = route.sequence.map(s => [s.latitude, s.longitude]);
          const polyline = L.polyline(latlngs, {
            color: '#009940',
            weight: 3,
            opacity: 0.7
          });
          
          polyline.bindPopup(`
            <strong>${route.name}</strong><br/>
            Vehículo: ${route.vehicle_matricula}<br/>
            Paradas: ${route.sequence.length}<br/>
            Estado: ${route.status}
          `);
          
          polyline.addTo(layersRef.current.routes);
          
          // Add markers for stops
          route.sequence.forEach((stop, idx) => {
            const marker = L.marker([stop.latitude, stop.longitude], {
              icon: L.divIcon({
                className: 'custom-route-marker',
                html: `<div style="background:#009940;color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;border:2px solid white;">${idx + 1}</div>`,
                iconSize: [24, 24]
              })
            });
            marker.bindPopup(`Parada ${idx + 1}: ${stop.ticket_code}`);
            marker.addTo(layersRef.current.routes);
          });
        }
      });
    }
    
    // Add GPS vehicles
    if (visibleLayers.vehicles) {
      layers.gps_vehicles.forEach(vehicle => {
        if (vehicle.lat && vehicle.lon) {
          const marker = L.marker([vehicle.lat, vehicle.lon], {
            icon: L.divIcon({
              className: 'custom-vehicle-marker',
              html: `<div style="background:#69C7EA;color:#374B67;border-radius:4px;padding:4px 8px;font-size:11px;font-weight:bold;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.2);">🚛 ${vehicle.plate}</div>`,
              iconSize: [80, 30]
            })
          });
          
          marker.bindPopup(`
            <strong>${vehicle.plate}</strong><br/>
            Velocidad: ${vehicle.speed || 0} km/h<br/>
            Última actualización: ${new Date(vehicle.ts).toLocaleTimeString()}<br/>
            Fuente GPS: ${vehicle.gps_source}
          `);
          
          marker.addTo(layersRef.current.vehicles);
        }
      });
    }
  };

  const toggleLayer = (layerName) => {
    setVisibleLayers(prev => ({ ...prev, [layerName]: !prev[layerName] }));
  };

  const handleSyncArcGIS = async () => {
    setSyncingArcGIS(true);
    try {
      const resp = await axios.post(`${API}/admin/arcgis/sync`, {}, { headers: authHeaders() });
      toast.success('Sincronización con ArcGIS completada');
    } catch (err) {
      toast.error('Error al sincronizar con ArcGIS');
    } finally {
      setSyncingArcGIS(false);
    }
  };

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-64px)] flex flex-col">
        {/* Controls */}
        <div className="bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchMapLayers}
              variant="outline"
              size="sm"
              disabled={loading}
              data-testid="map-refresh-button"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              <span className="ml-2">Actualizar</span>
            </Button>
            
            <div className="h-6 w-px bg-slate-300"></div>
            
            <button
              onClick={() => toggleLayer('solicitudes')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                visibleLayers.solicitudes ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
              }`}
              data-testid="map-toggle-solicitudes"
            >
              <MapPin size={14} />
              Solicitudes ({layers.solicitudes.length})
            </button>
            
            <button
              onClick={() => toggleLayer('routes')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                visibleLayers.routes ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
              }`}
              data-testid="map-toggle-routes"
            >
              <RouteIcon size={14} />
              Rutas ({layers.routes.length})
            </button>
            
            <button
              onClick={() => toggleLayer('vehicles')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                visibleLayers.vehicles ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-500'
              }`}
              data-testid="map-toggle-vehicles"
            >
              <Truck size={14} />
              Vehículos GPS ({layers.gps_vehicles.length})
            </button>
          </div>
          
          <Button
            onClick={handleSyncArcGIS}
            disabled={syncingArcGIS}
            className="bg-[#69C7EA] hover:bg-[#5AB5D8] text-[#374B67]"
            data-testid="map-sync-arcgis-button"
          >
            <RefreshCw size={16} className={syncingArcGIS ? 'animate-spin' : ''} />
            <span className="ml-2">Sincronizar ArcGIS</span>
          </Button>
        </div>
        
        {/* Map */}
        <div ref={mapRef} className="flex-1" data-testid="map-container"></div>
        
        {/* Legend */}
        <div className="bg-white border-t border-slate-200 p-3">
          <div className="flex items-center gap-6 text-xs">
            <span className="font-semibold text-slate-600">Leyenda:</span>
            {Object.entries(STATUS_COLORS).map(([estado, color]) => (
              <div key={estado} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                <span className="text-slate-600">{estado}</span>
              </div>
            ))}
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-[#009940] rounded-full"></div>
              <span className="text-slate-600">Rutas</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-[#69C7EA] rounded-full"></div>
              <span className="text-slate-600">Vehículos GPS</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
